import React, { useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../auth/AuthContext';
import {
  FaHome,
  FaUsers,
  FaShoppingCart,
  FaClipboardList,
  FaStore,
  FaBolt,
  FaChartLine,
  FaCog,
  FaTools,
  FaBox,
  FaFileInvoice,
  FaCheckCircle,
  FaUserShield,
  FaDatabase,
  FaChartPie,
  FaCreditCard,
  FaTag,
  FaComments,
  FaUser,
  FaUserClock,
  FaTruck,
  FaShieldAlt,
  FaExclamationTriangle,
  FaBalanceScale,
  FaMoneyBillWave,
  FaSignOutAlt,
  FaTimes,
  FaWarehouse,
  FaCar,
  FaChevronDown,
  FaChevronRight,
} from 'react-icons/fa';

const navBtn =
  'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left';
const navBtnActive = 'bg-red-50 text-red-700 font-semibold';
const navBtnIdle = 'text-slate-600 hover:bg-slate-100 hover:text-slate-900';

const Sidebar = ({ isOpen, setIsOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const [isUsersMenuOpen, setIsUsersMenuOpen] = useState(false);

  const menuItems = useMemo(() => ([
    { id: 'overview', label: 'Overview', icon: FaChartPie, path: '/admin/dashboard' },
    {
      id: 'users',
      label: 'Users',
      icon: FaUsers,
      children: [
        { id: 'vendors', label: 'Vendor', icon: FaStore, path: '/admin/dashboard/vendors' },
        { id: 'mechanics', label: 'Mechanic', icon: FaTools, path: '/admin/dashboard/mechanics' },
        { id: 'garages', label: 'Garage', icon: FaWarehouse, path: '/admin/dashboard/garages' },
        { id: 'shipping', label: 'Shipping', icon: FaTruck, path: '/admin/dashboard/shipping' },
        { id: 'customers', label: 'Customer', icon: FaUser, path: '/admin/dashboard/users' },
      ],
    },
    { id: 'orders', label: 'Orders', icon: FaShoppingCart, path: '/admin/dashboard/orders' },
    { id: 'availability', label: 'Used availability', icon: FaClipboardList, path: '/admin/dashboard/availability' },
    { id: 'vehicles', label: 'Vehicle Management', icon: FaCar, path: '/admin/dashboard/vehicles' },
    { id: 'catalog-gaps', label: 'Catalog gaps', icon: FaDatabase, path: '/admin/dashboard/catalog-gaps' },
    { id: 'seller-review-queue', label: 'Seller review', icon: FaShieldAlt, path: '/admin/dashboard/seller-review-queue' },
    { id: 'catalog-approvals', label: 'Profile E approvals', icon: FaBolt, path: '/admin/dashboard/catalog-approvals' },
    { id: 'jobcards-qc', label: 'QC Jobs', icon: FaCheckCircle, path: '/admin/dashboard/jobcards-qc' },
    { id: 'products', label: 'Products', icon: FaBox, path: '/admin/dashboard/products' },
    { id: 'categories', label: 'Categories', icon: FaDatabase, path: '/admin/dashboard/categories' },
    { id: 'payments', label: 'Payments', icon: FaCreditCard, path: '/admin/dashboard/payments' },
    { id: 'invoices', label: 'Invoices', icon: FaFileInvoice, path: '/admin/dashboard/invoices' },
    { id: 'marketing', label: 'Marketing', icon: FaTag, path: '/admin/dashboard/marketing' },
    { id: 'deals', label: 'Deals', icon: FaBolt, path: '/admin/dashboard/deals' },
    { id: 'combo-failures', label: 'Combo Failures', icon: FaBolt, path: '/admin/dashboard/combo-failures' },
    { id: 'deal-audit', label: 'Deal Audit', icon: FaFileInvoice, path: '/admin/dashboard/deal-audit' },
    { id: 'support', label: 'Support', icon: FaComments, path: '/admin/dashboard/support' },
    { id: 'fraud', label: 'Fraud Detection', icon: FaShieldAlt, path: '/admin/dashboard/fraud' },
    { id: 'risk', label: 'Risk & Suspension', icon: FaExclamationTriangle, path: '/admin/dashboard/risk' },
    { id: 'disputes', label: 'Dispute arbitration', icon: FaBalanceScale, path: '/admin/dashboard/disputes' },
    { id: 'comms-rules', label: 'Comms rules', icon: FaComments, path: '/admin/dashboard/comms-rules' },
    { id: 'commissions', label: 'Commissions', icon: FaMoneyBillWave, path: '/admin/dashboard/commissions' },
    { id: 'lucky-draw', label: 'Lucky Draw', icon: FaChartPie, path: '/admin/dashboard/lucky-draw' },
    { id: 'partner-applications', label: 'Partner applications', icon: FaUserClock, path: '/admin/dashboard/partner-applications' },
    { id: 'kyc', label: 'KYC Management', icon: FaUserShield, path: '/admin/dashboard/kyc' },
    { id: 'fines', label: 'Fines & Penalties', icon: FaFileInvoice, path: '/admin/dashboard/fines' },
    { id: 'analytics', label: 'Analytics', icon: FaChartLine, path: '/admin/dashboard/analytics' },
    { id: 'reports', label: 'Reports', icon: FaFileInvoice, path: '/admin/dashboard/reports' },
    { id: 'profile', label: 'My Profile', icon: FaUser, path: '/admin/dashboard/profile' },
    { id: 'settings', label: 'Settings', icon: FaCog, path: '/admin/dashboard/settings' },
  ]), []);

  const isActive = (path) => {
    if (path === '/admin/dashboard') {
      return location.pathname === '/admin/dashboard' || location.pathname === '/admin/dashboard/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside
        className={`fixed top-0 left-0 h-screen z-50 w-64 bg-white border-r border-slate-200 shadow-lg transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 lg:sticky lg:top-0 shrink-0`}
      >
        <div className="flex flex-col h-full min-h-0">
          <div className="flex items-center justify-between px-4 py-4 border-b border-slate-200 shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-600 flex items-center justify-center text-white shrink-0">
                <FaChartLine className="text-lg" />
              </div>
              <div>
                <h2 className="text-base font-bold text-slate-900">
                  Spare<span className="text-red-600">pé</span>
                </h2>
                <p className="text-xs text-slate-500">Admin console</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="lg:hidden p-2 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition"
              aria-label="Close menu"
            >
              <FaTimes className="text-lg" />
            </button>
          </div>

          <nav className="flex-1 min-h-0 overflow-y-auto p-3">
            <p className="px-3 mb-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">Menu</p>
            <ul className="space-y-1">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const hasChildren = Array.isArray(item.children) && item.children.length > 0;
                const childActive = hasChildren && item.children.some((child) => isActive(child.path));
                const active = hasChildren ? childActive : isActive(item.path);
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => {
                        if (hasChildren) {
                          setIsUsersMenuOpen((prev) => !prev);
                          return;
                        }
                        setIsUsersMenuOpen(false);
                        navigate(item.path);
                        setIsOpen(false);
                      }}
                      className={`${navBtn} ${active ? navBtnActive : navBtnIdle}`}
                    >
                      <Icon className={`text-base shrink-0 ${active ? 'text-red-600' : 'text-slate-400'}`} />
                      <span className="truncate flex-1">{item.label}</span>
                      {hasChildren && (
                        isUsersMenuOpen || childActive
                          ? <FaChevronDown className="text-xs shrink-0 text-slate-400" />
                          : <FaChevronRight className="text-xs shrink-0 text-slate-400" />
                      )}
                    </button>
                    {hasChildren && isUsersMenuOpen && (
                      <ul className="mt-1 ml-4 pl-2 border-l border-slate-200 space-y-0.5">
                        {item.children.map((child) => {
                          const ChildIcon = child.icon;
                          const childIsActive = isActive(child.path);
                          return (
                            <li key={child.id}>
                              <button
                                type="button"
                                onClick={() => {
                                  navigate(child.path);
                                  setIsOpen(false);
                                }}
                                className={`${navBtn} text-sm py-2 ${childIsActive ? navBtnActive : navBtnIdle}`}
                              >
                                <ChildIcon className={`text-sm shrink-0 ${childIsActive ? 'text-red-600' : 'text-slate-400'}`} />
                                <span className="truncate">{child.label}</span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </nav>

          <div className="shrink-0 p-3 border-t border-slate-200 bg-white space-y-1">
            <button
              type="button"
              onClick={() => { navigate('/'); setIsOpen(false); }}
              className={`${navBtn} ${navBtnIdle}`}
            >
              <FaHome className="text-base shrink-0 text-slate-400" />
              <span>Back to site</span>
            </button>
            <button
              type="button"
              onClick={() => { logout(); navigate('/'); }}
              className={`${navBtn} text-red-600 hover:bg-red-50 hover:text-red-700`}
            >
              <FaSignOutAlt className="text-base shrink-0" />
              <span>Log out</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
